/**
 * liveSpeech envelope → TranslationRecordView fragments / preview（design D14）
 */

import type {
  CrV3SessionSnapshot,
  LiveSpeechMessage,
  LiveSpeechViewerUpdate,
  ViewerSubtitleRow,
} from '../types'
import type { LiveSpeechEnvelopeData, SpeechTextCell } from '../utils/build-live-speech-envelope'
import type { ViewerLanguageItem } from '@/modules/speech-translation/api/viewer'

/** 与 Gateway livespeech_mapper._short_lang 对齐：zh-CN → zh */
export function shortLangCode(code: string): string {
  const trimmed = (code || '').trim()
  if (!trimmed) {
    return ''
  }
  return trimmed.split('-')[0].toLowerCase()
}

export function isEnvelopeData(data: unknown): data is LiveSpeechEnvelopeData {
  return typeof data === 'object' && data !== null && 'readText' in data
}

/** envelope 内所有可展示语种单元 */
export function envelopeCells(data: LiveSpeechEnvelopeData): SpeechTextCell[] {
  const cells: SpeechTextCell[] = []
  if (data.readText) {
    cells.push(data.readText)
  }
  if (data.listenText?.length) {
    cells.push(...data.listenText)
  }
  return cells
}

type DisplayCell = { languageCode: string; text?: string; content?: string }

/** D1 pickDisplayItem：按展示语言匹配 readText / listenText（或 legacy 扁平行数组） */
export function pickDisplayItem<T extends DisplayCell>(
  data: T[] | LiveSpeechEnvelopeData,
  displayLang: string,
): T | SpeechTextCell | null {
  const cells: DisplayCell[] = isEnvelopeData(data) ? envelopeCells(data) : data
  if (!cells?.length) {
    return null
  }
  const target = shortLangCode(displayLang)
  const matched = cells.find((item) => shortLangCode(item.languageCode) === target)
  return (matched ?? cells[0]) as T | SpeechTextCell | null
}

function cellText(cell: DisplayCell | SpeechTextCell | null): string {
  if (!cell) {
    return ''
  }
  return ('text' in cell ? cell.text : cell.content) ?? ''
}

/** pickDisplayItem → speechArrive 风格（PC 主持单列） */
export function pickDisplaySpeech(
  message: LiveSpeechMessage,
  displayLang: string,
): { type: 'speaking' | 'complete'; speaker: string; content: string } | null {
  const data = message.data
  if (!isEnvelopeData(data)) {
    return null
  }
  const item = pickDisplayItem(data, displayLang)
  const content = cellText(item).trim()
  if (!content) {
    return null
  }
  const duration = data.duration ?? 0
  return {
    type: duration >= 1 ? 'complete' : 'speaking',
    speaker: data.speeker ?? '',
    content,
  }
}

/** 将 liveSpeech envelope 转为 preview 或 fragment 更新 */
export function liveSpeechToViewerUpdate(
  message: LiveSpeechMessage,
): LiveSpeechViewerUpdate | null {
  const data = message.data
  if (!isEnvelopeData(data)) {
    return null
  }

  const isFinal = (data.duration ?? 0) >= 1
  const translations: Record<string, string> = {}

  for (const cell of data.listenText ?? []) {
    if (cell.text?.trim()) {
      translations[shortLangCode(cell.languageCode)] = cell.text
    }
  }
  if (data.readText?.text?.trim()) {
    translations[shortLangCode(data.readText.languageCode)] = data.readText.text
  }

  const row: ViewerSubtitleRow = {
    source_text: data.readText?.text ?? '',
    translations,
    speaker: data.speeker,
    ts: isFinal ? String(data.endTime ?? Date.now()) : undefined,
  }

  if (!row.source_text?.trim() && !Object.keys(translations).length) {
    return null
  }

  return { kind: isFinal ? 'fragment' : 'preview', row }
}

/** 观众端按目标语种取展示文本（短码对齐；无译文时回退 source_text） */
export function pickViewerRowLineText(
  row: { source_text?: string; translations?: Record<string, string> },
  displayLang: string,
): string {
  const target = shortLangCode(displayLang)
  if (!target) {
    return (row.source_text || '').trim()
  }
  const translations = row.translations ?? {}
  const direct = (translations[target] ?? translations[displayLang] ?? '').trim()
  if (direct) {
    return translations[target] ?? translations[displayLang] ?? direct
  }
  return (row.source_text || '').trim()
}

const SHORT_TO_BCP: Record<string, string> = {
  ar: 'ar-SA',
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ky: 'ky-KG',
  pt: 'pt-BR',
  ru: 'ru-RU',
  zh: 'zh-CN',
}

/** D19：H5/Android 听者 TTS BCP locale；session code2 优先，否则 short→BCP */
export function resolveViewerTtsLocale(
  listenLangShort: string,
  langs: {
    source: { languageCode: string; languageCode2?: string }
    targets: Array<{ languageCode: string; languageCode2?: string }>
  },
): string {
  const short = shortLangCode(listenLangShort)
  if (!short) {
    return 'en-US'
  }
  if (shortLangCode(langs.source.languageCode) === short) {
    const bcp = langs.source.languageCode2?.trim()
    if (bcp) {
      return bcp
    }
  }
  for (const target of langs.targets) {
    if (shortLangCode(target.languageCode) === short) {
      const bcp = target.languageCode2?.trim()
      if (bcp) {
        return bcp
      }
    }
  }
  if (listenLangShort.includes('-')) {
    return listenLangShort.trim()
  }
  return SHORT_TO_BCP[short] ?? 'en-US'
}

/** Web/H5 字幕行：与 PC host speechArrive 一致，前缀发言人姓名 */
export function formatViewerRowLineText(
  row: { source_text?: string; translations?: Record<string, string>; speaker?: string },
  displayLang: string,
): string {
  const content = pickViewerRowLineText(row, displayLang)
  if (!content) {
    return ''
  }
  const speaker = (row.speaker || '').trim()
  if (!speaker) {
    return content
  }
  return `${speaker}：${content}`
}

/**
 * 本地 SDK 行补齐 translations：识别原文常在 source_text，与下行 envelope 行为对齐。
 */
export function enrichLocalViewerRow(
  row: ViewerSubtitleRow,
  voiceLanguageCode: string,
): ViewerSubtitleRow {
  const voiceShort = shortLangCode(voiceLanguageCode)
  const translations = { ...(row.translations ?? {}) }
  const sourceText = (row.source_text ?? '').trim()
  if (sourceText && voiceShort && !translations[voiceShort]?.trim()) {
    translations[voiceShort] = sourceText
  }
  return { ...row, translations }
}

/** getSessionDataResult → 观众端语言列配置（列 code 与 liveSpeech 短码对齐） */
export function sessionSnapshotToLanguages(
  session: CrV3SessionSnapshot,
): {
  sourceLanguage: ViewerLanguageItem
  targetLanguages: ViewerLanguageItem[]
  /** 课堂源语 BCP locale（PTT SDK 用 languageCode2） */
  classroomWorkLangCode: string
} {
  // D11：优先读结构化 sourceLanguage / targetLanguage
  if (session.sourceLanguage?.languageCode) {
    const source = session.sourceLanguage
    const sourceShort = shortLangCode(source.languageCode)
    const sourceBcp = (source.languageCode2 || source.languageCode).trim()

    const seen = new Set<string>()
    const targetLanguages: ViewerLanguageItem[] = []
    for (const item of session.targetLanguage ?? []) {
      const code = shortLangCode(item.languageCode)
      if (!code || seen.has(code)) {
        continue
      }
      seen.add(code)
      targetLanguages.push({
        languageCode: code,
        languageCode2: item.languageCode2,
        languageName: item.languageName,
        languageEnName: item.languageEnName,
      })
    }
    if (!targetLanguages.length) {
      targetLanguages.push({ languageCode: 'en' })
    }

    return {
      sourceLanguage: {
        languageCode: sourceShort,
        languageCode2: source.languageCode2,
        languageName: source.languageName,
        languageEnName: source.languageEnName,
      },
      targetLanguages,
      classroomWorkLangCode: sourceBcp || 'zh-CN',
    }
  }

  // legacy pipe 回退（debug / 旧 Gateway）
  const sourceCode = shortLangCode(session.workLangCode || 'zh-CN')
  const rawTargets = (session.workLangListenCode || 'en')
    .split('|')
    .map((c) => shortLangCode(c))
    .filter(Boolean)

  const seen = new Set<string>()
  const targetLanguages: ViewerLanguageItem[] = []
  for (const languageCode of rawTargets.length ? rawTargets : ['en']) {
    if (seen.has(languageCode)) {
      continue
    }
    seen.add(languageCode)
    targetLanguages.push({ languageCode })
  }

  const legacySourceBcp = (session.workLangCode || 'zh-CN').trim() || 'zh-CN'
  return {
    sourceLanguage: { languageCode: sourceCode },
    targetLanguages,
    classroomWorkLangCode: legacySourceBcp,
  }
}

/** 合并 viewer 状态：preview 覆盖；fragment 追加并清空 preview */
export function applyViewerUpdate(
  state: {
    fragments: ViewerSubtitleRow[]
    preview: ViewerSubtitleRow | null
  },
  update: LiveSpeechViewerUpdate,
  maxLines: number,
): { fragments: ViewerSubtitleRow[]; preview: ViewerSubtitleRow | null } {
  if (update.kind === 'preview') {
    return { ...state, preview: update.row }
  }
  const fragments = [...state.fragments, update.row].slice(-maxLines)
  return { fragments, preview: null }
}
